import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  type FormControlProp,
  MonetaryInputField,
  SelectField,
  SubmitButton,
  Text,
  TextInputField,
} from '@/components';
import {
  useAccountTitles,
  useAccountingTypeOptions,
} from '@/features/master-table/hooks';
import { isBefore2025 } from '@/features/project/_utils/project';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { AmountUnitMultipliers, BigCalculator } from '@/utils';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CalculatedLabeledText } from '../../../../../../components/Form/CalculatedLabeledText';
import { FundInvestmentsFormSchema } from '../../../_models/project';

type FormValues = z.infer<typeof FundInvestmentsFormSchema>;

type Props = {
  initialValues: Partial<FormValues>;
  fiscalYear: number;
  onSubmit: (values: FormValues) => void;
};

export const FundInvestmentsForm: FC<Props> = ({
  initialValues,
  fiscalYear,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const accountingTypeOptions = useAccountingTypeOptions();
  const accountTitles = useAccountTitles();

  const {
    handleSubmit,
    watch,
    resetField,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(FundInvestmentsFormSchema),
  });

  const {
    fields: accountFields,
    append: appendAccount,
    remove: removeAccount,
  } = useFieldArray({
    control,
    name: 'accumulatedInvestmentsFromTheGovernment',
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Form.Item
        label={t(
          'accumulated-investment-amount-from-the-government-in-accounting-breakdown',
        )}
      >
        <Flex vertical gap={8}>
          {accountFields.map((item, index) => {
            const accountingId = watch(
              `accumulatedInvestmentsFromTheGovernment.${index}.accountingTypeId`,
            );

            return (
              <Card key={item.id}>
                <Box marginBottom={8}>
                  <Flex justify="flex-end">
                    <MinusCircleOutlined
                      onClick={() => {
                        removeAccount(index);
                      }}
                    />
                  </Flex>
                </Box>
                <Flex vertical gap={8}>
                  <SelectField
                    name={`accumulatedInvestmentsFromTheGovernment.${index}.accountingTypeId`}
                    label={t('accounting-name')}
                    control={control}
                    errors={errors}
                    options={accountingTypeOptions.data}
                    shouldCoexistOnChange
                    onChange={() => {
                      resetField(
                        `accumulatedInvestmentsFromTheGovernment.${index}.accountTitleId`,
                        { defaultValue: null },
                      );
                      resetField(
                        `accumulatedInvestmentsFromTheGovernment.${index}.optionalAccountingTypeName`,
                        { defaultValue: null },
                      );
                      resetField(
                        `accumulatedInvestmentsFromTheGovernment.${index}.optionalAccountTitleName`,
                        { defaultValue: null },
                      );
                    }}
                  />
                  <SelectField
                    name={`accumulatedInvestmentsFromTheGovernment.${index}.accountTitleId`}
                    label={t('account-title')}
                    control={control}
                    errors={errors}
                    options={accountTitles.data?.flatMap((d) =>
                      d.accountingId === accountingId
                        ? {
                            value: d.id,
                            label: d.name,
                          }
                        : [],
                    )}
                  />
                  <TextInputField
                    name={`accumulatedInvestmentsFromTheGovernment.${index}.optionalAccountingTypeName`}
                    label={t('optional-accounting-name')}
                    control={control}
                    errors={errors}
                    disabled={!!accountingId}
                    fallbackValue={null}
                  />
                  <TextInputField
                    name={`accumulatedInvestmentsFromTheGovernment.${index}.optionalAccountTitleName`}
                    label={t('optional-account-title')}
                    control={control}
                    errors={errors}
                    disabled={!!accountingId}
                    fallbackValue={null}
                  />
                  <MonetaryInputField
                    name={`accumulatedInvestmentsFromTheGovernment.${index}.amount`}
                    control={control}
                    label={t('amount')}
                    errors={errors}
                    unit={unit}
                    required
                  />
                </Flex>
              </Card>
            );
          })}
          <Button
            type="dashed"
            onClick={() => {
              appendAccount({
                accountingTypeId: null,
                accountTitleId: null,
                amount: null as any,
                optionalAccountingTypeName: null,
                optionalAccountTitleName: null,
              });
            }}
            block
            icon={<PlusOutlined />}
          >
            {t('add-accumulated-amount-in-accounting')}
          </Button>
        </Flex>
      </Form.Item>

      <CalculatedTotalAmount control={control} />

      <Divider />
      <Text variant="h5" text={t('investment-amount-by-year')} />

      {[0, 1, 2].map((idx) => (
        <MonetaryInputField
          key={idx}
          name={`annualInvestmentsFromTheGovernment.${idx}`}
          label={t('fiscal-year-with-value', { year: fiscalYear - 2 + idx })}
          control={control}
          errors={errors}
          unit={unit}
          disabled={idx === 0 && !isBefore2025({ fiscalYear })}
        />
      ))}

      <Divider />

      <MonetaryInputField
        name="accumulatedInvestmentFromPrivateSectors"
        label={t('accumulated-investment-amount-from-the-private-sectors')}
        control={control}
        errors={errors}
        unit={unit}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};

const CalculatedTotalAmount = ({
  control,
}: {
  control: FormControlProp<FormValues>;
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const investmentsFromTheGovernment = useWatch({
    control,
    name: 'accumulatedInvestmentsFromTheGovernment',
  });

  const totalAmount =
    investmentsFromTheGovernment?.reduce<number | null>((acc, investment) => {
      if (investment.amount == null) {
        return acc;
      }
      return BigCalculator.add(acc ?? 0, investment.amount);
    }, null) ?? null;

  return (
    <CalculatedLabeledText
      title={t('total-amount')}
      value={
        typeof totalAmount === 'number'
          ? BigCalculator.divide(
              totalAmount,
              AmountUnitMultipliers.thousand,
            )?.toFixed()
          : null
      }
      unit={unit}
    />
  );
};
